import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import ListingCard from '../components/ListingCard';
import { Icon } from '../tokens';
import TrolyLockup from '../components/TrolyLockup';
import { useTheme } from '../context/ThemeContext';
import './Home.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
// Tope de piezas en venta que se anteponen al feed (el mismo MAX_WINDOW que
// el backend permite pintar de una sola vez).
const SALES_LIMIT = 1000;

export default function Home() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [filter, setFilter]     = useState('all');
  const [listings, setListings] = useState([]);
  const [status, setStatus]     = useState('loading'); // loading | ok | error
  const gridRef = useRef(null);

  const FILTERS = [
    { key: 'all',     label: t('home.filters.all') },
    { key: 'Venta',   label: t('home.filters.sale') },
    { key: 'Cambio',  label: t('home.filters.trade') },
    { key: 'Mostrar', label: t('home.filters.display') },
  ];

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    // La config del panel admin decide la fuente del feed (catálogo vs.
    // publicaciones) y la ventana [homeStart, homeEnd) de piezas a mostrar.
    axios.get(`${API}/api/settings/public`)
      .then(({ data }) => (cancelled ? null : data))
      .catch(() => ({}))
      .then((cfg) => {
        if (cancelled || cfg === null) return null;
        const params = { limit: cfg?.homeLimit ?? 30, source: cfg?.homeSource || 'catalog' };
        // La ventana solo aplica al feed por defecto: una búsqueda arranca
        // desde el inicio para no ocultar resultados.
        if (!q && cfg?.homeStart > 0) params.offset = cfg.homeStart;
        if (q) params.search = q;
        const windowReq = axios.get(`${API}/api/listings`, { params })
          .then((r) => r.data.listings || []);
        // Piezas en venta con prioridad (toggle del panel): todas las
        // publicaciones activas en venta van antes que la ventana. No aplica a
        // búsquedas, que ya mezclan venta y catálogo con su propio orden.
        if (q || !cfg?.homeShowSales) return windowReq;
        const salesReq = axios.get(`${API}/api/listings`, {
          params: { source: 'publications', limit: SALES_LIMIT },
        }).then((r) => r.data.listings || []);
        return Promise.all([salesReq, windowReq]).then(([sales, win]) => {
          // La ventana no repite lo que ya salió arriba: ni la misma
          // publicación ni la pieza de catálogo a la que está enlazada.
          const seen = new Set(sales.flatMap((s) => [String(s._id), s.listing && String(s.listing)]));
          return [...sales, ...win.filter((c) => !seen.has(String(c._id)))];
        });
      })
      .then((cards) => {
        if (cancelled || !cards) return;
        setListings(cards);
        setStatus('ok');
      })
      .catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, [q]);

  const filtered = useMemo(() => {
    if (filter === 'all') return listings;
    return listings.filter((l) => l.action === filter);
  }, [filter, listings]);

  const gate = () => { if (!user) navigate('/trigger'); };

  // Las rutas protegidas mandan al visitante al trigger; el resto es público.
  const go = (path) => () => navigate(user ? path : '/trigger');
  const scrollToCatalog = () => gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // Cada tarjeta habla de un feature que Troly ya tiene y lleva al lugar donde
  // se usa: valor total (perfil), catálogo, carpetas (subida), venta/cambio y club.
  const FEATURES = [
    {
      icon: Icon.chart(26),
      title: t('home.features.value_title'),
      body: t('home.features.value_desc'),
      cta: t('home.features.value_cta'),
      onClick: go('/profile'),
      wide: true,
    },
    {
      icon: Icon.search(26),
      title: t('home.features.prices_title'),
      body: t('home.features.prices_desc'),
      cta: t('home.features.prices_cta'),
      onClick: scrollToCatalog,
      wide: true,
    },
    {
      icon: Icon.folder(26),
      title: t('home.features.organize_title'),
      body: t('home.features.organize_desc'),
      cta: t('home.features.organize_cta'),
      onClick: go('/upload'),
    },
    {
      icon: Icon.swap(26),
      title: t('home.features.trade_title'),
      body: t('home.features.trade_desc'),
      cta: t('home.features.trade_cta'),
      onClick: () => navigate('/precios'),
    },
    {
      icon: Icon.network(26),
      title: t('home.features.community_title'),
      body: t('home.features.community_desc'),
      cta: t('home.features.community_cta'),
      onClick: go('/mensajes'),
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t('home.meta_title')}</title>
        <meta name="description" content={t('home.meta_desc')} />
        <link rel="canonical" href="https://troly.club" />
      </Helmet>

      <main>
        <section className="home-hero container fade-up">
          <p className="home-hero__eyebrow">{t('home.hero_eyebrow')}</p>
          <h1 className="home-hero__h1">
            {t('home.hero_title')} <span style={{ color: 'var(--teal)' }}>{t('home.hero_accent')}</span>
          </h1>
          <p className="home-hero__sub">
            {t('home.hero_sub')}
          </p>
        </section>

        <section className="home-chips container fade-up" style={{ animationDelay: '.08s' }}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`chip${filter === f.key ? ' chip--active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
          {q && (
            <button
              className="chip chip--active"
              onClick={() => setSearchParams({})}
              title={t('home.clear_search')}
            >
              {t('home.search_query_chip')}: "{q}" ✕
            </button>
          )}
        </section>

        <section ref={gridRef} className="home-grid container fade-up" style={{ animationDelay: '.14s' }}>
          {status === 'loading' && (
            <p className="home-empty">{t('home.loading')}</p>
          )}
          {status === 'error' && (
            <p className="home-empty home-empty--err">{t('home.load_error')}</p>
          )}
          {status === 'ok' && filtered.length === 0 && (
            <p className="home-empty">{t('home.empty_category')}</p>
          )}
          {status === 'ok' && filtered.map((l, i) => (
            <div key={l._id} className="fade-up" style={{ animationDelay: `${0.04 * i}s` }}>
              <ListingCard listing={l} />
            </div>
          ))}
        </section>

        <section className="home-features">
          <div className="container">
            <p className="home-features__eyebrow">{t('home.features.eyebrow')}</p>
            <h2 className="home-features__title">{t('home.features.title')}</h2>
            <p className="home-features__sub">{t('home.features.subtitle')}</p>

            <div className="home-features__grid">
              {FEATURES.map((f) => (
                <article key={f.title} className={`feat fade-up${f.wide ? ' feat--wide' : ''}`}>
                  <div className="feat__icon">{f.icon}</div>
                  <h3 className="feat__title">{f.title}</h3>
                  <p className="feat__body">{f.body}</p>
                  <button type="button" className="feat__cta" onClick={f.onClick}>
                    {f.cta} <span aria-hidden="true">→</span>
                  </button>
                </article>
              ))}
            </div>
          </div>
        </section>

        <footer className="home-footer">
          <div className="container home-footer__top">
            <div className="home-footer__brand">
              <TrolyLockup size="22px" inverse={theme === 'dark'} className="home-footer__wordmark" />
              <p>{t('home.footer.tagline')}</p>
            </div>
            <div className="home-footer__cols">
              {[
                { title: t('home.footer.platform'), links: [{ label: t('home.footer.raffle'), to: '/sorteo' }, { label: t('home.footer.pricing'), to: '/precios' }, { label: t('home.footer.security') }] },
                { title: t('home.footer.company'),  links: [{ label: t('home.footer.about') }, { label: t('home.footer.blog') }, { label: t('home.footer.careers') }] },
                { title: t('home.footer.legal'),    links: [{ label: t('home.footer.privacy') }, { label: t('home.footer.terms'), to: '/terminos' }] },
              ].map((col) => (
                <div key={col.title}>
                  <h4>{col.title}</h4>
                  {col.links.map((l) => (
                    l.to
                      ? <Link key={l.label} to={l.to}>{l.label}</Link>
                      : <a key={l.label} href="#.">{l.label}</a>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="container home-footer__bottom">
            <span>{t('home.footer.copyright')}</span>
            <button className="footer-community" onClick={gate}>{t('home.community')} →</button>
          </div>
        </footer>
      </main>
    </>
  );
}
